// Undo/redo history for the skin editor. Pure — no React, no DOM.
//
// DESIGN: snapshots with structural sharing. The history holds whole `SkinDocument`s, but a document
// is immutable and an edit replaces only the ONE layer buffer it touches (see layers.ts), so each
// entry costs one 16 KB layer for a pixel edit and ~nothing for a rename / visibility / reorder.
// Memory is bounded twice: at most MAX_HISTORY entries, and at most HISTORY_BYTE_BUDGET of DISTINCT
// pixel buffers across the whole history (oldest entries are dropped first).
//
// Model: `present` lives outside (the editor's current document); `past` holds what undo returns
// to, newest LAST; `future` holds what redo returns to, newest LAST. Recording clears `future`.
//
// (The previous implementation kept one list with an index and PUSHED the pre-stroke state before a
// stroke but the post-edit state after a fill: redo after a stroke re-applied nothing, and undoing a
// fill that followed a stroke jumped back past the stroke.)

import type { SkinDocument } from './layers';

export const MAX_HISTORY = 100;
export const HISTORY_BYTE_BUDGET = 8 * 1024 * 1024; // 512 distinct layer buffers

export interface History<T> {
  past: T[];
  future: T[];
}

export function emptyHistory<T>(): History<T> {
  return { past: [], future: [] };
}

function docBuffers(doc: SkinDocument): Uint8ClampedArray[] {
  return doc.layers.map(l => l.pixels);
}

/** Distinct pixel-buffer bytes held by a list of documents. */
export function historyBytes(docs: readonly SkinDocument[]): number {
  const seen = new Set<Uint8ClampedArray>();
  let bytes = 0;
  for (const d of docs) {
    for (const b of docBuffers(d)) {
      if (!seen.has(b)) { seen.add(b); bytes += b.byteLength; }
    }
  }
  return bytes;
}

/**
 * Record `previous` (the state as it was BEFORE the edit being committed) so undo can return to it.
 * Clears redo. Trims the oldest entries past the count and byte limits.
 *
 * An entry is a `SkinDocument`, or anything that carries one — the editor records `{ doc, modelType }`
 * so undoing a skin load also restores the arm model. `docOf` says where the document is.
 */
export function recordHistory<T = SkinDocument>(
  h: History<T>,
  previous: T,
  limits: { maxEntries?: number; byteBudget?: number } = {},
  docOf: (entry: T) => SkinDocument = entry => entry as unknown as SkinDocument,
): History<T> {
  const maxEntries = limits.maxEntries ?? MAX_HISTORY;
  const byteBudget = limits.byteBudget ?? HISTORY_BYTE_BUDGET;
  const past = h.past.concat([previous]);
  while (past.length > maxEntries) past.shift();
  while (past.length > 1 && historyBytes(past.map(docOf)) > byteBudget) past.shift();
  return { past, future: [] };
}

/** Step back. Returns null when there is nothing to undo. */
export function undoHistory<T>(h: History<T>, present: T): { history: History<T>; present: T } | null {
  if (h.past.length === 0) return null;
  const past = h.past.slice(0, -1);
  const prev = h.past[h.past.length - 1];
  return { history: { past, future: h.future.concat([present]) }, present: prev };
}

/** Step forward. Returns null when there is nothing to redo. */
export function redoHistory<T>(h: History<T>, present: T): { history: History<T>; present: T } | null {
  if (h.future.length === 0) return null;
  const future = h.future.slice(0, -1);
  const next = h.future[h.future.length - 1];
  return { history: { past: h.past.concat([present]), future }, present: next };
}

export function canUndo<T>(h: History<T>): boolean {
  return h.past.length > 0;
}

export function canRedo<T>(h: History<T>): boolean {
  return h.future.length > 0;
}
